import { TextField } from '@mui/material'
import { GRID_FORM_CONFIG_T } from '../../../../common/FormGenerator'
import { AutocompleteInput } from '../../../../components/ui-kit/AutocompleteInput'
import { AvatarUpload } from '../../../../components/ui-kit/AvatarUpload'
import { PROFESSION_OPTIONS } from '../../../../consts/professionsPtczh'
import { russiaRegionsOptions } from '../../../../consts/russiaRegions'
import { USER_STATUS_OPTIONS } from '../../../../consts/userStatus'
import type { UserFormValues } from '../ZodValidationSchema'

export type UserSettingsBlockName =
  | 'image'
  | 'surname'
  | 'name'
  | 'otch'
  | 'status'
  | 'slogan'
  | 'region'
  | 'city'
  | 'work'
  | 'description'
  | 'full_description'

const imageConfig: GRID_FORM_CONFIG_T<UserFormValues> = [
  [
    {
      id: 1,
      controllerName: 'image',
      RenderElement: AvatarUpload,
      renderProps: ({ formValues }) => {
        const image = formValues.image
        const isEmptyAvatar =
          image == null ||
          (typeof image === 'string' && image.trim().length === 0)

        return {
          hideRemove: true,
          ...(isEmptyAvatar
            ? {
                helperText:
                  'Аватар был удалён при удалении аккаунта. Загрузите новое фото.',
              }
            : {}),
        }
      },
    },
  ],
]

const surnameConfig: GRID_FORM_CONFIG_T<UserFormValues> = [
  [
    {
      id: 2,
      controllerName: 'surname',
      RenderElement: TextField,
      renderProps: () => ({
        placeholder: 'Фамилия',
        fullWidth: true,
        size: 'small',
        inputProps: { maxLength: 120 },
      }),
    },
  ],
]

const nameConfig: GRID_FORM_CONFIG_T<UserFormValues> = [
  [
    {
      id: 3,
      controllerName: 'name',
      RenderElement: TextField,
      renderProps: () => ({
        placeholder: 'Имя',
        fullWidth: true,
        size: 'small',
        inputProps: { maxLength: 120 },
      }),
    },
  ],
]

const otchConfig: GRID_FORM_CONFIG_T<UserFormValues> = [
  [
    {
      id: 4,
      controllerName: 'otch',
      RenderElement: TextField,
      renderProps: () => ({
        placeholder: 'Отчество',
        fullWidth: true,
        size: 'small',
        inputProps: { maxLength: 120 },
      }),
    },
  ],
]

const statusConfig: GRID_FORM_CONFIG_T<UserFormValues> = [
  [
    {
      id: 5,
      controllerName: 'status',
      RenderElement: AutocompleteInput,
      renderProps: () => ({
        options: USER_STATUS_OPTIONS,
        placeholder: 'Статус участия',
        fullWidth: true,
        size: 'small',
        disableClearable: true,
      }),
    },
  ],
]

const sloganConfig: GRID_FORM_CONFIG_T<UserFormValues> = [
  [
    {
      id: 9,
      controllerName: 'slogan',
      RenderElement: TextField,
      renderProps: () => ({
        placeholder: 'В поиске крутых проектов',
        fullWidth: true,
        size: 'small',
        inputProps: { maxLength: 255 },
      }),
    },
  ],
]

const regionConfig: GRID_FORM_CONFIG_T<UserFormValues> = [
  [
    {
      id: 7,
      controllerName: 'region',
      RenderElement: AutocompleteInput,
      renderProps: () => ({
        options: russiaRegionsOptions,
        fullWidth: true,
        size: 'small',
        placeholder: 'Регион',
      }),
    },
  ],
]

const cityConfig: GRID_FORM_CONFIG_T<UserFormValues> = [
  [
    {
      id: 8,
      controllerName: 'city',
      RenderElement: TextField,
      renderProps: () => ({
        placeholder: 'Населённый пункт',
        fullWidth: true,
        size: 'small',
      }),
    },
  ],
]

const workConfig: GRID_FORM_CONFIG_T<UserFormValues> = [
  [
    {
      id: 11,
      controllerName: 'work',
      RenderElement: AutocompleteInput,
      renderProps: () => ({
        options: PROFESSION_OPTIONS,
        fullWidth: true,
        size: 'small',
        freeSolo: true,
        placeholder: 'Выберите или введите профессию',
      }),
    },
  ],
]

const descriptionConfig: GRID_FORM_CONFIG_T<UserFormValues> = [
  [
    {
      id: 10,
      controllerName: 'description',
      RenderElement: TextField,
      renderProps: () => ({
        placeholder: 'Например: опыт, интересы, чем занимаетесь',
        fullWidth: true,
        multiline: true,
        minRows: 2,
        size: 'small',
        inputProps: { maxLength: 500 },
      }),
    },
  ],
]

const fullDescriptionConfig: GRID_FORM_CONFIG_T<UserFormValues> = [
  [
    {
      id: 12,
      controllerName: 'full_description',
      RenderElement: TextField,
      renderProps: () => ({
        placeholder: 'Расскажите о себе подробнее',
        fullWidth: true,
        multiline: true,
        minRows: 4,
        size: 'small',
      }),
    },
  ],
]

const formConfigs: Record<UserSettingsBlockName, GRID_FORM_CONFIG_T<unknown>> = {
  image: imageConfig as GRID_FORM_CONFIG_T<unknown>,
  surname: surnameConfig as GRID_FORM_CONFIG_T<unknown>,
  name: nameConfig as GRID_FORM_CONFIG_T<unknown>,
  otch: otchConfig as GRID_FORM_CONFIG_T<unknown>,
  status: statusConfig as GRID_FORM_CONFIG_T<unknown>,
  slogan: sloganConfig as GRID_FORM_CONFIG_T<unknown>,
  region: regionConfig as GRID_FORM_CONFIG_T<unknown>,
  city: cityConfig as GRID_FORM_CONFIG_T<unknown>,
  work: workConfig as GRID_FORM_CONFIG_T<unknown>,
  description: descriptionConfig as GRID_FORM_CONFIG_T<unknown>,
  full_description: fullDescriptionConfig as GRID_FORM_CONFIG_T<unknown>,
}

export const getFormConfig = (blockName: UserSettingsBlockName) => formConfigs[blockName]
